import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchParsingCases } from './fetchParsingCases';
import {
  type ListParsingCasesParams,
  type PaginatedParsingCasesResponse,
} from './validations';

/**
 * Generates the query options for fetching a paginated list of parsing cases.
 *
 * @param params - Filter and pagination parameters
 * @param options - Additional options for the query
 * @returns The query options including queryKey, queryFn, and other settings
 */
export const getParsingCasesQueryOptions = <
  TData = PaginatedParsingCasesResponse,
>({
  params,
  options,
}: {
  params: ListParsingCasesParams;
  options?: Omit<
    UseQueryOptions<PaginatedParsingCasesResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchParsingCases(params, { signal }),
    queryKey: queryKeysDeliveryNote.getAllParsingCases(params),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query hook for fetching a paginated list of parsing cases.
 *
 * @param params - Filter and pagination parameters
 * @param options - Additional React Query options
 * @returns Query result containing paginated parsing cases
 */
export const useQueryParsingCases = (
  params: ListParsingCasesParams,
  options?: Omit<
    UseQueryOptions<
      PaginatedParsingCasesResponse,
      Error,
      PaginatedParsingCasesResponse
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getParsingCasesQueryOptions<PaginatedParsingCasesResponse>({
      params,
      options,
    }),
  );
